import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { listMenus } from '../../api/menu';
import { listItems, listUoms } from '../../api/masterData';
import { computeRecipeCost, createRecipeVersion, listCostSnapshots, listRecipeLines, listRecipes, listRecipeVersions } from '../../api/recipe';
import { usePermissions } from '../../context/PermissionContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { CostSnapshot, Item, Menu, Recipe, RecipeLine, RecipeVersion, Uom } from '../../types/domain';

function formatRupiah(value: number): string {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 2 }).format(value);
}

interface MenuRow {
  menu: Menu;
  recipe: Recipe | null;
}

/**
 * Recipes / BOM (FASE 7, migration 0039). One row per Menu — a menu with no
 * recipe yet is still listed (creating its first version implicitly
 * creates the `recipes` shell row server-side, fn_create_recipe_version_checked).
 * recipes/recipe_versions are tenant-wide.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function RecipesPage() {
  const { hasPermission } = usePermissions();
  const [menus, setMenus] = useState<Menu[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<Uom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MenuRow | null>(null);

  const canRead = hasPermission('recipe', 'read');
  const canWrite = hasPermission('recipe', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [menuRows, recipeRows, itemRows, uomRows] = await Promise.all([listMenus(), listRecipes(), listItems(), listUoms()]);
      setMenus(menuRows);
      setRecipes(recipeRows);
      setItems(itemRows);
      setUoms(uomRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const rows: MenuRow[] = useMemo(
    () => menus.map((menu) => ({ menu, recipe: recipes.find((r) => r.menu_id === menu.menu_id) ?? null })),
    [menus, recipes]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.menu.name.toLowerCase().includes(q) || r.menu.menu_code.toLowerCase().includes(q));
  }, [rows, search]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Recipes / BOM" subtitle="Resep bahan baku per menu." />
        <EmptyState text="Anda tidak memiliki akses untuk melihat resep." variant="section" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Recipes / BOM" subtitle="Resep bahan baku per menu — dasar perhitungan HPP. Migrasi 0039." />
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <ListToolbar search={search} onSearchChange={setSearch} searchPlaceholder="Cari menu..." resultCount={total} />
          {rows.length === 0 && <EmptyState text="Belum ada menu — buat menu terlebih dahulu di Menu Master." />}
          {rows.length > 0 && total === 0 && <EmptyState text="Tidak ada menu yang cocok dengan pencarian." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Menu</th>
                  <th style={thStyle}>Status Resep</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <ClickableRow key={row.menu.menu_id} onClick={() => setSelected(row)}>
                    <td style={tdStyle}>{row.menu.name}</td>
                    <td style={tdStyle}>{row.recipe ? <StatusBadge status="Ada Resep" /> : <StatusBadge status="Belum Ada Resep" />}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <RecipeDetailDrawer
        row={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        items={items}
        uoms={uoms}
        canWrite={canWrite}
        canComputeCost={hasPermission('cost_snapshot', 'create')}
        canReadCost={hasPermission('cost_snapshot', 'read')}
        onChanged={() => void load()}
      />
    </div>
  );
}

interface DraftLine {
  itemId: string;
  uomId: string;
  qty: string;
}

function RecipeDetailDrawer({
  row,
  open,
  onClose,
  items,
  uoms,
  canWrite,
  canComputeCost,
  canReadCost,
  onChanged,
}: {
  row: MenuRow | null;
  open: boolean;
  onClose: () => void;
  items: Item[];
  uoms: Uom[];
  canWrite: boolean;
  canComputeCost: boolean;
  canReadCost: boolean;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<'lines' | 'hpp'>('lines');
  const [versions, setVersions] = useState<RecipeVersion[] | null>(null);
  const [lines, setLines] = useState<RecipeLine[] | null>(null);
  const [snapshots, setSnapshots] = useState<CostSnapshot[] | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const effectiveVersion = versions?.find((v) => v.status === 'ACTIVE') ?? null;

  useEffect(() => {
    setVersions(null);
    setLines(null);
    setSnapshots(null);
    setShowBuilder(false);
    setTab('lines');
    if (!row?.recipe) return;
    listRecipeVersions(row.recipe.recipe_id)
      .then(setVersions)
      .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
  }, [row]);

  useEffect(() => {
    if (!effectiveVersion) return;
    listRecipeLines(effectiveVersion.recipe_version_id)
      .then(setLines)
      .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
    if (canReadCost) {
      listCostSnapshots(effectiveVersion.recipe_version_id)
        .then(setSnapshots)
        .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVersion?.recipe_version_id, canReadCost]);

  if (!row) return null;
  const itemName = (id: string) => items.find((i) => i.item_id === id)?.name ?? id;
  const uomCode = (id: string) => uoms.find((u) => u.uom_id === id)?.code ?? id;
  const latestSnapshot = snapshots?.[0] ?? null;

  return (
    <Drawer open={open} onClose={onClose} title={row.menu.name}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--alex-border)' }}>
        {(['lines', 'hpp'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              border: 'none',
              background: 'none',
              padding: '8px 10px',
              fontSize: 'var(--alex-fs-caption)',
              fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
              borderBottom: tab === t ? '2px solid var(--alex-primary)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t === 'lines' ? 'Resep / Bahan' : 'HPP'}
          </button>
        ))}
      </div>

      {loadErr && <ErrorNote text={loadErr} />}

      {tab === 'lines' && (
        <>
          {effectiveVersion ? (
            <>
              <DrawerField label="Versi Aktif" value={`v${effectiveVersion.version_no} — berlaku sejak ${effectiveVersion.effective_from}`} />
              <DrawerField label="Hasil (Yield)" value={effectiveVersion.yield_qty} />
              {lines && lines.length > 0 && (
                <table style={{ ...tableStyle, marginBottom: 12 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Bahan</th>
                      <th style={thStyle}>Qty / Yield</th>
                      <th style={thStyle}>Satuan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.recipe_line_id}>
                        <td style={tdStyle}>{itemName(l.item_id)}</td>
                        <td style={tdStyle}>{l.qty_per_yield}</td>
                        <td style={tdStyle}>{uomCode(l.uom_id)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {lines === null && <LoadingNote />}
            </>
          ) : (
            <EmptyState text="Belum ada resep untuk menu ini." variant="compact" />
          )}
          {canWrite && (
            <Button variant={showBuilder ? 'ghost' : 'secondary'} onClick={() => setShowBuilder((v) => !v)} style={{ marginTop: 8 }}>
              {showBuilder ? 'Batal' : effectiveVersion ? '+ Versi Resep Baru' : '+ Buat Resep'}
            </Button>
          )}
          {showBuilder && (
            <RecipeVersionBuilder
              menuId={row.menu.menu_id}
              items={items}
              uoms={uoms}
              onSaved={() => {
                setShowBuilder(false);
                onChanged();
                if (row.recipe) {
                  listRecipeVersions(row.recipe.recipe_id).then(setVersions).catch(() => {});
                } else {
                  onClose();
                }
              }}
            />
          )}
        </>
      )}

      {tab === 'hpp' && (
        <>
          {!effectiveVersion && <EmptyState text="Belum ada resep — HPP tidak dapat dihitung." variant="compact" />}
          {effectiveVersion && !canReadCost && <EmptyState text="Anda tidak memiliki akses melihat HPP." variant="compact" />}
          {effectiveVersion && canReadCost && (
            <>
              {latestSnapshot ? (
                <>
                  <DrawerField
                    label="HPP per Porsi"
                    value={
                      <span>
                        {formatRupiah(latestSnapshot.unit_cost)}
                        {latestSnapshot.is_preliminary && (
                          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: 'var(--alex-warning, #b45309)' }}>PERKIRAAN</span>
                        )}
                      </span>
                    }
                  />
                  <DrawerField label="Dihitung pada" value={formatDateTime(latestSnapshot.computed_at)} />
                  <DrawerField label="Metode" value={latestSnapshot.cost_basis.method === 'LAST_PURCHASE_PRICE' ? 'Harga Pembelian Terakhir' : latestSnapshot.cost_basis.method} />
                  <table style={{ ...tableStyle, marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Bahan</th>
                        <th style={thStyle}>Qty (satuan dasar)</th>
                        <th style={thStyle}>Harga Satuan</th>
                        <th style={thStyle}>Biaya</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestSnapshot.cost_basis.lines.map((l, i) => (
                        <tr key={i}>
                          <td style={tdStyle}>{itemName(l.item_id)}</td>
                          <td style={tdStyle}>{l.qty_base_uom}</td>
                          <td style={tdStyle}>{l.has_price_data ? formatRupiah(l.unit_price) : 'Belum tersedia'}</td>
                          <td style={tdStyle}>{l.has_price_data ? formatRupiah(l.line_cost) : 'Belum tersedia'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <EmptyState text="HPP belum pernah dihitung untuk versi resep ini." variant="compact" />
              )}
              {canComputeCost && (
                <ComputeCostButton
                  recipeVersionId={effectiveVersion.recipe_version_id}
                  onDone={() => {
                    listCostSnapshots(effectiveVersion.recipe_version_id).then(setSnapshots).catch(() => {});
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
}

function ComputeCostButton({ recipeVersionId, onDone }: { recipeVersionId: string; onDone: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div style={{ marginTop: 12 }}>
      <Button
        variant="primary"
        loading={loading}
        onClick={async () => {
          setLoading(true);
          setError(null);
          try {
            await computeRecipeCost(recipeVersionId);
            onDone();
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          } finally {
            setLoading(false);
          }
        }}
      >
        Hitung Ulang HPP
      </Button>
      {error && <ErrorNote text={error} />}
    </div>
  );
}

function RecipeVersionBuilder({
  menuId,
  items,
  uoms,
  onSaved,
}: {
  menuId: string;
  items: Item[];
  uoms: Uom[];
  onSaved: () => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [yieldQty, setYieldQty] = useState('1');
  const [lines, setLines] = useState<DraftLine[]>([{ itemId: '', uomId: '', qty: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { itemId: '', uomId: '', qty: '' }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const usable = lines.filter((l) => l.itemId && l.uomId && l.qty);
    if (usable.length === 0) {
      setError('Resep harus memiliki minimal 1 bahan.');
      return;
    }
    if (usable.some((l) => Number(l.qty) <= 0)) {
      setError('Qty bahan harus lebih dari 0.');
      return;
    }
    const itemIds = usable.map((l) => l.itemId);
    if (new Set(itemIds).size !== itemIds.length) {
      setError('Setiap bahan hanya boleh muncul satu kali dalam satu resep.');
      return;
    }
    const yq = Number(yieldQty);
    if (!Number.isFinite(yq) || yq <= 0) {
      setError('Yield harus lebih dari 0.');
      return;
    }
    setSubmitting(true);
    try {
      await createRecipeVersion(
        menuId,
        effectiveFrom,
        yq,
        usable.map((l) => ({ item_id: l.itemId, uom_id: l.uomId, qty_per_yield: Number(l.qty) }))
      );
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginTop: 12, padding: 14 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 130 }}>
          <FieldLabel label="Berlaku mulai">
            <TextInput type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
          </FieldLabel>
        </div>
        <div style={{ width: 90 }}>
          <FieldLabel label="Yield">
            <TextInput type="number" min="0.01" step="0.01" value={yieldQty} onChange={(e) => setYieldQty(e.target.value)} required />
          </FieldLabel>
        </div>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
          <div style={{ flex: 2 }}>
            <FieldLabel label="Bahan">
              <Select value={line.itemId} onChange={(e) => updateLine(i, { itemId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {items.map((it) => (
                  <option key={it.item_id} value={it.item_id}>
                    {it.item_code} — {it.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 90 }}>
            <FieldLabel label="UOM">
              <Select value={line.uomId} onChange={(e) => updateLine(i, { uomId: e.target.value })} required>
                <option value="" disabled>
                  Pilih...
                </option>
                {uoms.map((u) => (
                  <option key={u.uom_id} value={u.uom_id}>
                    {u.code}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ width: 80 }}>
            <FieldLabel label="Qty">
              <TextInput type="number" min="0.01" step="0.01" value={line.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} required />
            </FieldLabel>
          </div>
          {lines.length > 1 && (
            <Button type="button" variant="ghost" onClick={() => removeLine(i)}>
              Hapus
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="secondary" onClick={addLine} style={{ marginBottom: 10 }}>
        + Bahan
      </Button>
      <div>
        <Button type="submit" variant="primary" loading={submitting}>
          Simpan Resep
        </Button>
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
